import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const WEEK_IN_MS = 7 * DAY_IN_MS;
const MONTH_IN_MS = 30 * DAY_IN_MS;
const HOUR_IN_MS = 60 * 60 * 1000;
const DEFAULT_PERIOD_IN_MS = 14 * DAY_IN_MS;

const PERIOD_UNIT_IN_MS = {
  day: DAY_IN_MS,
  days: DAY_IN_MS,
  d: DAY_IN_MS,
  week: WEEK_IN_MS,
  weeks: WEEK_IN_MS,
  w: WEEK_IN_MS,
  month: MONTH_IN_MS,
  months: MONTH_IN_MS,
  m: MONTH_IN_MS,
  hour: HOUR_IN_MS,
  hours: HOUR_IN_MS,
  h: HOUR_IN_MS,
};

const sanitizePeriodValue = rawValue =>
  typeof rawValue === 'string' ? rawValue.trim() : rawValue;

const toTimestamp = value => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime();
  }

  if (typeof value === 'number') {
    if (Number.isNaN(value)) return null;
    // Assume values larger than seconds precision are already in ms
    if (value > 1e12) return value;
    if (value > 1e9) return value * 1000;
    // Treat small positive numbers as days
    if (value > 0) return value * DAY_IN_MS;
    return null;
  }

  if (typeof value === 'string') {
    const parsedValue = Date.parse(value);
    if (!Number.isNaN(parsedValue)) return parsedValue;
  }

  return null;
};

const mapResourceType = type => {
  if (!type || typeof type !== 'string') return '';
  const normalized = type.replace(/[\s_-]/g, '').toLowerCase();

  switch (normalized) {
    case 'practice':
    case 'practices':
      return 'practices';
    case 'assessment':
    case 'assessments':
      return 'assessments';
    case 'series':
    case 'audioseries':
    case 'videoseries':
      return 'series';
    default:
      return normalized;
  }
};

const parseNewResourcePeriod = periodConfig => {
  if (periodConfig == null) return DEFAULT_PERIOD_IN_MS;

  if (Array.isArray(periodConfig)) {
    for (let index = 0; index < periodConfig.length; index += 1) {
      const parsed = parseNewResourcePeriod(periodConfig[index]);
      if (parsed != null) return parsed;
    }
    return DEFAULT_PERIOD_IN_MS;
  }

  if (typeof periodConfig === 'object') {
    const value = Number(
      periodConfig.value ?? periodConfig.amount ?? periodConfig.period,
    );
    const unit = `${periodConfig.unit ??
      periodConfig.type ??
      ''}`.toLowerCase();

    if (!Number.isNaN(value) && value > 0) {
      const unitMs = PERIOD_UNIT_IN_MS[unit] || DAY_IN_MS;
      return value * unitMs;
    }

    return DEFAULT_PERIOD_IN_MS;
  }

  if (typeof periodConfig === 'number') {
    if (periodConfig <= 0) return 0;
    return periodConfig * DAY_IN_MS;
  }

  if (typeof periodConfig === 'string') {
    const sanitized = sanitizePeriodValue(periodConfig)
      .replace(/[,;]+/g, ' ')
      .replace(/[^\dA-Za-z.\s]/g, '')
      .trim();

    if (!sanitized) return DEFAULT_PERIOD_IN_MS;

    const match = sanitized.match(/^(\d+(?:\.\d+)?)\s*([A-Za-z]+)?$/u);

    if (match) {
      const value = parseFloat(match[1]);
      const unitKey = (match[2] || 'days').toLowerCase();
      const unitMs =
        PERIOD_UNIT_IN_MS[unitKey] ||
        PERIOD_UNIT_IN_MS[`${unitKey}s`] ||
        DAY_IN_MS;

      if (!Number.isNaN(value) && value > 0) {
        return value * unitMs;
      }
    }
  }

  return DEFAULT_PERIOD_IN_MS;
};

const getResourcePublishedTimestamp = resource => {
  if (!resource) return null;

  const directPaths = ['fields.publicationDate', 'publicationDate'];

  for (let index = 0; index < directPaths.length; index += 1) {
    const timestamp = toTimestamp(_get(resource, directPaths[index]));
    if (timestamp) return timestamp;
  }

  return null;
};

export const shouldShowNewResourceLabel = ({
  resource,
  resourceType,
  featuresSiteConfig,
}) => {
  if (!resource || _isEmpty(featuresSiteConfig)) return false;

  const config = _get(featuresSiteConfig, 'config.newResourceLabel', {});
  if (!_get(config, 'enabled')) return false;

  const normalizedType = mapResourceType(
    resourceType ||
      _get(resource, 'type') ||
      _get(resource, 'fields.type') ||
      _get(resource, 'fields.mediaType'),
  );

  const configuredTypes = (_get(config, 'resourceTypes') || []).map(type =>
    mapResourceType(type),
  );

  if (!_isEmpty(configuredTypes) && !configuredTypes.includes(normalizedType)) {
    return false;
  }
  const publishedTimestamp = getResourcePublishedTimestamp(resource);
  if (!publishedTimestamp) return false;

  const now = Date.now();
  if (Number.isNaN(publishedTimestamp) || now < publishedTimestamp) {
    return false;
  }

  const periodInMs = parseNewResourcePeriod(_get(config, 'period'));
  if (!periodInMs || periodInMs <= 0) return false;

  return now - publishedTimestamp <= periodInMs;
};
